import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import PageLoader from './components/PageLoader';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import './dark-mode.css';
import './styles/mobile-responsive.css';
import EditJobPage from './pages/EditJobPage';
import Onboarding from './components/Onboarding';

// New Dashboard and Profile Components

const JobSeekerDashboard = React.lazy(() => import('./pages/JobSeekerDashboard'));
const CompanyDashboard = React.lazy(() => import('./pages/CompanyDashboard'));
const JobSeekerProfilePage = React.lazy(() => import('./pages/JobSeekerProfilePage'));
const CompanyProfilePage = React.lazy(() => import('./pages/CompanyProfilePage'));

// Lazy load pages for code splitting and better performance
const Home = React.lazy(() => import('./pages/Home'));
const JobListings = React.lazy(() => import('./pages/JobListings'));
const Help = React.lazy(() => import('./pages/Help'));
const Logout = React.lazy(() => import('./pages/Logout'));
const JobDetails = React.lazy(() => import('./pages/JobDetails'));
const Login = React.lazy(() => import('./pages/Login'));
const LoginJobSeeker = React.lazy(() => import('./pages/LoginJobSeeker'));
const LoginEmployer = React.lazy(() => import('./pages/LoginEmployer'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const About = React.lazy(() => import('./pages/About'));
const CandidatesPage = React.lazy(() => import('./pages/CandidatesPage'));
const PostJobPage = React.lazy(() => import('./pages/PostJobPage'));
const MyApplicationsPage = React.lazy(() => import('./pages/MyApplicationsPage'));
const MyFeedbackPage = React.lazy(() => import('./pages/MyFeedbackPage'));
const JobApplicantsPage = React.lazy(() => import('./pages/JobApplicantsPage'));
const ApplicationDetailsPage = React.lazy(() => import('./pages/ApplicationDetailsPage'));
const EmailVerificationPage = React.lazy(() => import('./pages/EmailVerificationPage'));

const AppContent: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user, userType, loading: authLoading } = useAuth();

  React.useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  if (authLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <Onboarding />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/jobs" element={<JobListings />} />
          <Route path="/job/:id" element={<JobDetails />} />
          <Route path="/my-applications" element={<MyApplicationsPage />} />
          <Route path="/my-feedback" element={<MyFeedbackPage />} />
          <Route path="/company/jobs/:jobId/applicants" element={<JobApplicantsPage />} />
          <Route path="/application/:id" element={<ApplicationDetailsPage />} />
          
          {/* Conditional Dashboard Route */}
          <Route
            path="/dashboard"
            element={
              user ? (
                userType === 'jobseeker' ? (
                  <JobSeekerDashboard />
                ) : userType === 'company' ? (
                  <CompanyDashboard />
                ) : (
                  <Navigate to="/" replace /> // Redirect if user type is unknown or not logged in
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Conditional Profile Route */}
          <Route
            path="/profile"
            element={
              user ? (
                userType === 'jobseeker' ? (
                  <JobSeekerProfilePage />
                ) : userType === 'company' ? (
                  <CompanyProfilePage />
                ) : (
                  <Navigate to="/" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/help" element={<Help />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/jobseeker" element={<LoginJobSeeker />} />
          <Route path="/login/employer" element={<LoginEmployer />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/candidates" element={<CandidatesPage />} />
          <Route path="/edit-job/:jobId" element={<EditJobPage />} />
          <Route path="/post-job" element={<PostJobPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <ErrorBoundary>
              <Navbar />
            </ErrorBoundary>
            <div className="container-fluid">
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
            </div>
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;